import { useEffect, useMemo, useState } from "react";
import { Badge } from "@/components/shadcn/badge";
import { Input } from "@/components/shadcn/input";
import { Pagination, PaginationContent, PaginationItem, PaginationNext, PaginationPrevious } from "@/components/shadcn/pagination";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/shadcn/select";
import { DataTable, TableColumn, TextCell } from "@/components/common/Table";

import { AddAiUserAccessDialog } from "./AddAiUserAccessDialog";
import { AiSectionHeading } from "./AiSectionHeading";
import { RevokeAiUserAccessDialog } from "./RevokeAiUserAccessDialog";
import { RevokeAllAiUserAccessDialog } from "./RevokeAllAiUserAccessDialog";
import type { AiUserAccessRow, SortKey, UserAccessFilter } from "../types";

const dateFormatter = new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" });

// The API does not currently support server-side paging for the user access list, so it always
// returns every granted user - this paginates the already-fetched, already-filtered rows client-side.
const PAGE_SIZE = 10;

const UserAccessDataTable = DataTable<AiUserAccessRow, SortKey>;

export const AiUserSubscriptionsTable = ({
  rows,
  existingUserIds,
  search,
  userAccessFilter,
  sortColumn,
  sortAsc,
  isLoading,
  error,
  onSearchChange,
  onUserAccessFilterChange,
  onSort,
  onUserAccessChanged,
}: {
  rows: AiUserAccessRow[];
  existingUserIds: string[];
  search: string;
  userAccessFilter: UserAccessFilter;
  sortColumn: SortKey;
  sortAsc: boolean;
  isLoading?: boolean;
  error?: string | null;
  onSearchChange: (value: string) => void;
  onUserAccessFilterChange: (value: UserAccessFilter) => void;
  onSort: (newSortColumn: SortKey, newSortAsc: boolean) => void;
  onUserAccessChanged: () => Promise<void>;
}) => {
  const hasUsers = existingUserIds.length > 0;

  const [page, setPage] = useState(1);

  // Reset back to the first page whenever the filtered result set changes shape, so the user
  // isn't left stranded on a page that no longer has any rows.
  useEffect(() => {
    setPage(1);
  }, [search, userAccessFilter, sortColumn, sortAsc]);

  const totalCount = rows.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  // Clamp if the current page becomes out of range for another reason (e.g. the last user on the
  // last page was revoked).
  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const pagedRows = useMemo(
    () => rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [rows, page]
  );

  const columns: TableColumn<AiUserAccessRow, SortKey>[] = [
    { key: "displayName", header: "Name", sortKey: "displayName", render: (item) => <TextCell value={item.displayName} /> },
    { key: "emailAddress", header: "Email", sortKey: "emailAddress", render: (item) => <TextCell value={item.emailAddress || item.username} /> },
    {
      key: "enabled",
      header: "Account enabled",
      sortKey: "enabled",
      render: (item) => (
        <Badge variant={item.enabled ? "success" : "secondary"}>
          {item.enabled ? "Enabled" : "Disabled"}
        </Badge>
      ),
    },
    {
      key: "grantedAtUtc",
      header: "Granted",
      sortKey: "grantedAtUtc",
      render: (item) => <TextCell value={dateFormatter.format(new Date(item.grantedAtUtc))} />,
    },
    {
      key: "actions",
      header: "",
      isAction: true,
      render: (item) => <RevokeAiUserAccessDialog user={item} callBack={onUserAccessChanged} />,
    },
  ];

  return (
    <div className="min-w-0">
      <div className="flex flex-row items-start justify-between gap-4">
        <div>
          <AiSectionHeading title="User access" subtitle="Users who have been granted allow-listed access to AI for this account." />
        </div>
        <div className="flex flex-row items-start gap-2">
          <AddAiUserAccessDialog existingUserIds={existingUserIds} callBack={onUserAccessChanged} />
          <RevokeAllAiUserAccessDialog userCount={existingUserIds.length} callBack={onUserAccessChanged} />
        </div>
      </div>
      {error && <div className="mb-4 text-sm text-destructive">{error}</div>}
      <div className="mb-4 grid gap-3 md:grid-cols-2">
        <Input
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder="Filter by name, email, or username"
          disabled={!hasUsers}
        />
        <Select value={userAccessFilter} onValueChange={(value) => onUserAccessFilterChange(value as UserAccessFilter)} disabled={!hasUsers}>
          <SelectTrigger>
            <SelectValue placeholder="Account status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="enabled">Enabled</SelectItem>
            <SelectItem value="disabled">Disabled</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <UserAccessDataTable
        columns={columns}
        data={pagedRows}
        sortColumn={sortColumn}
        sortAsc={sortAsc}
        onSort={onSort}
        isLoading={isLoading}
        skeletonRows={5}
      />
      {!isLoading && totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-table-border bg-muted/30 px-4 py-3">
          <span className="text-sm text-muted-foreground">
            Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, totalCount)} of {totalCount} users
          </span>
          <Pagination className="mx-0 w-auto">
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  onClick={(event) => { event.preventDefault(); setPage((current) => current - 1); }}
                  aria-disabled={page === 1}
                  className={page === 1 ? "pointer-events-none opacity-40" : "cursor-pointer"}
                />
              </PaginationItem>
              <PaginationItem>
                <span className="text-sm font-medium px-2">
                  Page {page} of {totalPages}
                </span>
              </PaginationItem>
              <PaginationItem>
                <PaginationNext
                  onClick={(event) => { event.preventDefault(); setPage((current) => current + 1); }}
                  aria-disabled={page >= totalPages}
                  className={page >= totalPages ? "pointer-events-none opacity-40" : "cursor-pointer"}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </div>
  );
};
